import { createContext, useContext, type ReactElement, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { useIsNarrow } from './useIsNarrow';

/**
 * Перенос главного действия страницы в полосу навигации на узком экране.
 *
 * Зачем. На телефоне ряд с кнопкой «Добавить пользователя» занимал отдельную строку
 * над данными — при том что справа в полосе стояло имя аккаунта, которое смотрят
 * раз в смену. Имя переезжает в бургер, кнопка — на его место: строка экономится
 * целиком, а частое действие оказывается там, где раньше лежало редкое.
 *
 * Почему порталом, а не двумя копиями разметки. Кнопка ОДНА — та же самая, с тем же
 * обработчиком и тем же состоянием. Отрисовать её и в шапке, и в полосе, спрятав одну
 * из них через CSS, значило бы завести две кнопки в дереве доступности: скринридер
 * прочитал бы обе, а автотест нашёл бы две.
 */

const SlotContext = createContext<HTMLElement | null>(null);

export function PageActionSlotProvider({
  slot,
  children,
}: {
  slot: HTMLElement | null;
  children: ReactNode;
}): ReactElement {
  return <SlotContext.Provider value={slot}>{children}</SlotContext.Provider>;
}

/**
 * Отрисовать действия страницы: в полосе навигации на узком экране, на месте —
 * на широком.
 *
 * Возвращает `null`, если на узком экране слот ещё не смонтирован: рисовать кнопку
 * «пока не туда» нельзя — она мигнёт не на своём месте.
 */
export function PageActions({ children }: { children: ReactNode }): ReactElement | null {
  const slot = useContext(SlotContext);
  const narrow = useIsNarrow();

  if (!narrow) {
    return <>{children}</>;
  }
  return slot ? createPortal(children, slot) : null;
}
